/**
 * A drift log — the thing you salvage.
 *
 * It is a small buoyant Vessel with the bed-grounding turned up: while it rests
 * on the sand it is pinned by friction and won't budge until the tug leans on
 * the tow line, and once dragged into deep water it floats and tows like any
 * other hull. No propulsion of its own; it only moves because something pulls
 * it. The visual is a single cut cylinder with paler end grain, sized off a
 * seed so a beach full of them doesn't look stamped.
 */

import { CylinderGeometry, Group, Mesh, MeshStandardNodeMaterial, Vector3, Color } from 'three/webgpu'
import { Vessel } from '../physics/Vessel.ts'
import { bedHeight } from '../world/Beach.ts'

export interface LogAttachments {
  /** The end the tow line is made fast to. */
  towPoint: Vector3
}

const _tow = new Vector3()

export class Log extends Vessel {
  readonly group: Group
  readonly attachments: LogAttachments

  constructor(seed = 1) {
    // Deterministic size jitter from the seed.
    const rng = ((seed * 9301 + 49297) % 233280) / 233280
    const length = 8 + rng * 5
    const radius = 0.5 + rng * 0.25

    super({
      name: 'log',
      mass: 3200 + rng * 2600,
      length,
      beam: radius * 2,
      draft: radius * 1.4,
      dragForward: 900,
      dragLateral: 3400,
      dragYaw: 4.0e5,
      lateralResistanceOffset: 0.2,
      buoyancySamples: [3, 1],
      // Stuck until the tug puts real strain on the wire, then it grinds free.
      groundFriction: 2.8e5,
      yawInertiaScale: 1.2,
    })

    // Logs live only in beach mode, so they always read the shore directly.
    this.bedSampler = bedHeight

    this.group = new Group()
    this.group.name = 'log'
    const barkMat = new MeshStandardNodeMaterial({ color: new Color('#5f4327') })
    barkMat.roughness = 0.95
    const grainMat = new MeshStandardNodeMaterial({ color: new Color('#b79366') })
    grainMat.roughness = 0.8

    // Cylinder axis is Y; rotate so it lies along the body +Z (its length).
    const trunk = new Mesh(new CylinderGeometry(radius, radius * 0.92, length, 14), barkMat)
    trunk.rotation.x = Math.PI / 2
    trunk.castShadow = true
    trunk.receiveShadow = true
    this.group.add(trunk)

    for (const end of [-1, 1]) {
      const cap = new Mesh(new CylinderGeometry(radius * (end < 0 ? 1 : 0.92), radius * (end < 0 ? 1 : 0.92), 0.15, 14), grainMat)
      cap.rotation.x = Math.PI / 2
      cap.position.z = (end * length) / 2
      this.group.add(cap)
    }

    this.attachments = { towPoint: new Vector3(0, 0, length * 0.42) }
  }

  /** True once the log is floating free of the sand — ready to tow off. */
  get afloat(): boolean {
    return this.grounding < 0.05
  }

  /** World position of the tow bollard, for the hook-range test. */
  towPointWorld(target: Vector3): Vector3 {
    return this.localToWorld(this.attachments.towPoint, target)
  }

  /** Sync the visual transform from the physics state. */
  syncVisual(): void {
    this.group.position.copy(this.position)
    this.group.quaternion.copy(this.quaternion)
  }

  /** Convenience for the objective: distance from a world point to the tow end. */
  towDistanceTo(x: number, z: number): number {
    this.towPointWorld(_tow)
    return Math.hypot(_tow.x - x, _tow.z - z)
  }
}
